import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

const Setup: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Setup — Sprout Docs</title>
        <meta
          name="description"
          content="How a school admin gets started with Sprout: sign up, run the onboarding wizard, invite teachers, and invite parents."
        />
        <link rel="canonical" href="https://sprout-ui-pi.vercel.app/docs/getting-started/setup" />
      </Helmet>

      <h1>Setup</h1>
      <p className="lead">
        This guide walks through what happens between signing up and having your
        first classroom ready for observations. It takes most schools under
        thirty minutes.
      </p>

      <h2>1. Sign up and pick a plan</h2>
      <p>
        Head to <Link to="/pricing">Pricing</Link> and choose a tier. Seedling
        is free forever and covers unlimited children with text-only
        observations — most schools start there. You can upgrade later without
        losing data.
      </p>

      <h2>2. Run the admin onboarding wizard</h2>
      <p>
        On first login as an organisation admin, you'll be taken through a
        short wizard that creates your school's tenant schema and lets you set
        up your first classrooms. You'll be asked for:
      </p>
      <ul>
        <li>School name and a short code (used internally to namespace your data).</li>
        <li>One or more classrooms — for example "Grade R Robins" or "Grade 1 Daisies".</li>
        <li>Optionally, your initial roster of children. You can also import this from a CSV later.</li>
      </ul>

      <h2>3. Invite teachers</h2>
      <p>
        From the <strong>Users</strong> page, send an email invitation to each
        teacher. They'll set their own password and get the Teacher role by
        default, which lets them create observations and assessments for the
        classrooms they're assigned to.
      </p>

      <h2>4. Invite parents</h2>
      <p>
        From a child's profile, click <strong>Invite Guardian</strong> and
        enter the parent's email. Sprout sends a tokenised invitation link;
        the parent sets a password and lands in the Guardian Portal where
        they'll see only published observations and assessments for their own
        child.
      </p>

      <h2>That's it</h2>
      <p>
        Once teachers are in and parents have been invited, your team can
        start recording observations. Read{' '}
        <Link to="/docs/getting-started/first-observation">Your First Observation</Link> next.
      </p>
    </>
  );
};

export default Setup;
